import { Component, Input, Output, EventEmitter, computed, signal, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-pagination',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="flex flex-col sm:flex-row items-center justify-between gap-4 py-3 px-4 border-t border-slate-200/90 bg-slate-50/50 rounded-b-3xl text-xs font-sans">
      <!-- Showing X to Y of Z entries -->
      <div class="flex items-center gap-2 text-slate-500">
        @if (totalItems > 0) {
          <span>
            Showing <strong class="text-slate-800 font-bold">{{ startItem }}</strong> to
            <strong class="text-slate-800 font-bold">{{ endItem }}</strong> of
            <strong class="text-slate-900 font-extrabold">{{ totalItems }}</strong> entries
          </span>
        } @else {
          <span class="italic text-slate-400">No entries to display</span>
        }

        <!-- Page size selector -->
        <div class="flex items-center gap-1.5 ml-3 pl-3 border-l border-slate-200">
          <span class="text-slate-400 text-[11px]">Per page:</span>
          <select
            [ngModel]="pageSize"
            (ngModelChange)="onPageSizeSelect($event)"
            class="px-2 py-1 rounded-lg border border-slate-300 bg-white text-slate-800 text-xs font-semibold focus:outline-none focus:border-emerald-500 cursor-pointer shadow-2xs"
          >
            @for (opt of pageSizeOptions; track opt) {
              <option [value]="opt">{{ opt }}</option>
            }
          </select>
        </div>
      </div>

      <!-- Page navigation controls -->
      @if (totalPages > 1) {
        <div class="flex items-center gap-1">
          <!-- Previous Button -->
          <button
            type="button"
            (click)="goToPage(currentPage - 1)"
            [disabled]="currentPage <= 1"
            title="Previous page"
            class="p-1.5 rounded-lg border border-slate-200 bg-white text-slate-600 hover:text-slate-900 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition cursor-pointer shadow-2xs inline-flex items-center gap-1"
          >
            <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
            <span class="hidden sm:inline text-[11px] font-medium pr-1">Prev</span>
          </button>

          <!-- Numbered Pages -->
          @for (item of visiblePages(); track $index) {
            @if (item === -1) {
              <span class="px-2 text-slate-400 font-bold">...</span>
            } @else {
              <button
                type="button"
                (click)="goToPage(item)"
                class="min-w-8 h-8 px-2 rounded-lg text-xs font-bold transition cursor-pointer flex items-center justify-center shadow-2xs"
                [ngClass]="currentPage === item
                  ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-xs font-extrabold'
                  : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-100 hover:text-slate-900'"
              >
                {{ item }}
              </button>
            }
          }

          <!-- Next Button -->
          <button
            type="button"
            (click)="goToPage(currentPage + 1)"
            [disabled]="currentPage >= totalPages"
            title="Next page"
            class="p-1.5 rounded-lg border border-slate-200 bg-white text-slate-600 hover:text-slate-900 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition cursor-pointer shadow-2xs inline-flex items-center gap-1"
          >
            <span class="hidden sm:inline text-[11px] font-medium pl-1">Next</span>
            <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      }
    </div>
  `
})
export class PaginationComponent {
  @Input() currentPage = 1;
  @Input() pageSize = 10;
  @Input() totalItems = 0;
  @Input() pageSizeOptions = [10, 25, 50, 100];

  @Output() pageChange = new EventEmitter<number>();
  @Output() pageSizeChange = new EventEmitter<number>();

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalItems / (this.pageSize || 10)));
  }

  get startItem(): number {
    if (this.totalItems === 0) return 0;
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get endItem(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalItems);
  }

  visiblePages = computed(() => {
    const total = this.totalPages;
    const current = this.currentPage;
    const delta = 1;

    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    const pages: number[] = [];
    pages.push(1);

    if (current > delta + 2) {
      pages.push(-1); // ellipsis
    }

    const start = Math.max(2, current - delta);
    const end = Math.min(total - 1, current + delta);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (current < total - (delta + 1)) {
      pages.push(-1); // ellipsis
    }

    pages.push(total);
    return pages;
  });

  goToPage(p: number) {
    if (p >= 1 && p <= this.totalPages && p !== this.currentPage) {
      this.pageChange.emit(p);
    }
  }

  onPageSizeSelect(size: number | string) {
    const s = Number(size);
    if (s && s !== this.pageSize) {
      this.pageSizeChange.emit(s);
    }
  }
}
